<template>
  <details
    class="expansion-panels-panel"
    :itemscope="itemScope"
    :itemprop="itemProp"
    :itemtype="itemType"
    :open="open"
    @toggle="handleToggle"
    @click="handleClick"
  >
    <summary class="expansion-panels-panel__title">
      <slot name="title" />
      <IconBase
        class="expansion-panels-panel__title-icon"
        name="arrow-right-20-icon"
        :size="20"
        :rotate="open ? 180 : 0"
      />
    </summary>
    <slot class="expansion-panels-panel__content" />
  </details>
</template>

<script lang="ts" setup>
  import IconBase from '@/shared/icons/IconBase.vue';

  export interface Props {
    /**
     * Item is open.
     */
    open?: boolean;

    /**
     * Enable schema item scope.
     */
    itemScope?: boolean;

    /**
     * Schema item property name.
     */
    itemProp?: string;

    /**
     * Schema item type e.g. {@link https://schema.org/Question}.
     */
    itemType?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    itemProp: undefined,
    itemType: undefined,
    open: false,
  });

  defineSlots<{
    /**
     * Content.
     */
    default(): unknown;

    /**
     * Title.
     */
    title(): string;
  }>();

  const emit = defineEmits<{
    /**
     * Sends an event when the item state is toggled.
     *
     * @param opened "expand" event occurs
     */
    toggled: [opened: boolean];

    /**
     * Item title clicked.
     */
    clicked: [];
  }>();

  const handleToggle = (e: ToggleEvent) => {
    e.preventDefault();

    const open = e.newState === 'open';

    if (open !== props.open) {
      emit('toggled', open);
    }
  };

  const handleClick = () => {
    emit('clicked');
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;

  .expansion-panels-panel {
    border-bottom: 1px solid var(--alias-color-border-default);

    &:last-child {
      border-bottom: 0;
    }
  }

  .expansion-panels-panel__title {
    display: flex;
    gap: var(--alias-space-24);
    align-items: flex-start;
    justify-content: space-between;
    padding: var(--alias-space-24) 0;
    cursor: pointer;
    user-select: none;

    &:focus {
      outline: none;
      border: none;
    }

    &::-webkit-details-marker {
      display: none;
    }

    @include legacy-typography.base-500-400-semi;

    @include legacy-adaptivity.mobile {
      padding: var(--alias-space-20) 0;

      @include legacy-typography.base-400-300-semi;
    }
  }

  .expansion-panels-panel__title-icon {
    flex-grow: 0;
    flex-shrink: 0;
  }
</style>
